import React, { useEffect, useState } from 'react';
import { Container, Grid, makeStyles } from '@material-ui/core';
import Page from 'src/components/Page';
import NotFoundCard from 'src/components/NotFoundCard';
import { useNavigate, useParams } from 'react-router';
import toast from 'react-hot-toast';
import Admin from 'src/api/admin';
import Profile from './Profile';
import ProfileDetails from './ProfileDetails';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const AdminView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const [notFound, setNotFound] = useState(false);
  const [pageName, setPageName] = useState('Admin');
  const [admin, setAdmin] = useState({});
  const [profileFormdata, setProfileFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const getUser = async userId => {
    try {
      setNotFound(false);
      const res = await Admin.getAdmin(userId);
      setProfileFormData(res.data);
      setAdmin(res.data);
      setPageName(`${res.data.firstName} ${res.data.lastName}`);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        return;
      }
      // Interceptor already toasted network errors (ISSUE-0201).
      if (err.response) {
        toast.error(
          err.response.data?.errors?.[0] ||
            err.response.data?.message ||
            'Something went wrong'
        );
      }
    }
  };
  useEffect(() => {
    getUser(id);
  }, [id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateProfileDetails = async () => {
    const changedFields = Object.entries(profileFormdata).reduce(
      (fields, curKeyValuePair) => {
        const [key, value] = curKeyValuePair;
        if (value !== admin[key]) fields[key] = value;
        return fields;
      },
      {}
    );

    // there are no changed fields, no point sending request
    if (!Object.keys(changedFields).length) return;

    try {
      setIsUpdating(true);
      // The API returns the edited admin object, not a one-item array.
      const updatedAdmin = await Admin.superAdminEditAdmin(
        admin.id,
        changedFields
      );
      setAdmin(updatedAdmin);
      setProfileFormData(updatedAdmin);
      setIsUpdating(false);
    } catch (error) {
      setIsUpdating(false);
      // Let ProfileDetails show the error and stay in edit mode (ISSUE-0145).
      throw error;
    }
  };

  const activateAdmin = async active => {
    try {
      const updatedAdmin = await Admin.superAdminEditAdmin(admin.id, {
        active
      });
      setAdmin(updatedAdmin);
      setProfileFormData(updatedAdmin);
      toast.success('Status Updated');
    } catch (err) {
      // Interceptor already toasted network errors (ISSUE-0201).
      if (err.response) {
        toast.error(
          err.response.data?.errors?.[0] ||
            err.response.data?.message ||
            'Something went wrong'
        );
      }
    }
  };
  const sendResetPassword = async () => {
    try {
      await Admin.superAdminResetAdminPassword({ email: admin.email });
      toast.success('Reset password sent');
    } catch (err) {
      // Interceptor already toasted network errors (ISSUE-0201).
      if (err.response) {
        toast.error(
          err.response.data?.errors?.[0] ||
            err.response.data?.message ||
            'Something went wrong'
        );
      }
    }
  };

  if (notFound) {
    return (
      <NotFoundCard
        title="Admin"
        message="Admin not found."
        backLabel="Back to admins"
        onBack={() => navigate('/app/admins')}
      />
    );
  }

  return (
    <Page className={classes.root} title={pageName}>
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item lg={4} md={6} xs={12}>
            <Profile
              admin={profileFormdata}
              activateAdmin={activateAdmin}
              sendResetPassword={sendResetPassword}
            />
          </Grid>
          <Grid item lg={8} md={6} xs={12}>
            <ProfileDetails
              formData={profileFormdata}
              admin={profileFormdata}
              onChange={handleChange}
              onSave={updateProfileDetails}
              isUpdating={isUpdating}
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default AdminView;
